import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { EyeIcon } from '@/ui/icons';
import { api } from '@/utils/api';

type Post = {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  coverImageUrl?: string;
  publishedAt?: string;
  viewCount?: number;
};

export function CategoryPage() {
  const { slug } = useParams();
  const [name, setName] = useState('');
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const BASE_ORIGIN = 'https://esdfl.net';
  const absolutize = (url?: string) => {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) return BASE_ORIGIN + url;
    return BASE_ORIGIN + '/' + url;
  };
  const title = useMemo(() => (name ? `${name} - Edirne Süleyman Demirel Fen Lisesi | ESDFL` : 'Kategori - Edirne Süleyman Demirel Fen Lisesi | ESDFL'), [name]);
  const description = useMemo(() => (name ? `${name} kategorisindeki yazılar.` : 'Kategori yazıları.'), [name]);
  const siteOrigin = BASE_ORIGIN;
  const catUrl = useMemo(() => siteOrigin + '/kategori/' + (slug ?? ''), [siteOrigin, slug]);
  const jsonLdBreadcrumb = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteOrigin + '/' },
      { '@type': 'ListItem', position: 2, name: name || 'Kategori', item: catUrl }
    ]
  }), [siteOrigin, name, catUrl]);
  const jsonLdItemList = useMemo(() => posts.length ? ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: posts.map((p, idx) => {
      const url = siteOrigin + '/yazi/' + p.slug;
      return {
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'Article',
          '@id': url,
          url,
          headline: p.title
        }
      };
    })
  }) : null, [posts, siteOrigin]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await api.get(`/api/public/category/${slug}`);
        if (!ignore) {
          setName(res.category?.name ?? 'Kategori');
          setPosts(res.posts ?? []);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [slug]);

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={catUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>
        {jsonLdItemList && <script type="application/ld+json">{JSON.stringify(jsonLdItemList)}</script>}
      </Helmet>
      <h1 className="text-2xl md:text-3xl font-serif font-bold mb-6">{name}</h1>
      {loading && <div>Yükleniyor…</div>}
      {!loading && posts.length === 0 && <div>Bu kategoride içerik yok.</div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {posts.map((post) => (
          <Link to={`/yazi/${post.slug}`} key={post.id} className="card overflow-hidden hover:shadow-md transition">
            {post.coverImageUrl && (
              <img src={absolutize(post.coverImageUrl)} alt={post.title} className="h-44 w-full object-cover" />
            )}
            <div className="p-4">
              <h2 className="text-lg font-semibold mb-1">{post.title}</h2>
              {post.excerpt && <p className="text-sm text-gray-600 line-clamp-3">{post.excerpt}</p>}
              <div className="mt-2 text-sm text-gray-600 inline-flex items-center gap-1">
                <EyeIcon /> {post.viewCount ?? 0}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}


